import { isNotNil } from 'es-toolkit';

import { cleanStringFromNonDigits } from '@hrlogics/helper-utils';
import type { GeneratedQueryHookType } from '@hrlogics/react-apollo-v3';

import { SSN_LENGTH } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';

import {
  GetEmployeeForPeoAccessSearchQuery,
  GetEmployeeForPeoAccessSearchQueryVariables,
  PeoStaffBlacklistedEmployeeKindEnum,
  useGetEmployeeForPeoAccessSearchQuery,
} from 'src/api';
import { EmployeeBlacklistItem } from 'src/components/peoStaff/AccessRestrictionModeFormPart/types';
import { useAutocompleteFieldWithQuery } from 'src/hooks/forms';
import { formatMaskedSsnNumber } from 'src/utils/ssn';

// The generated hook marks baseOptions as required (the query has a required variable),
// which doesn't structurally match GeneratedQueryHookType. useAutocompleteFieldWithQuery
// always invokes the hook with an options object, so widening the type is safe.
const useSearchQuery = useGetEmployeeForPeoAccessSearchQuery as GeneratedQueryHookType<
  GetEmployeeForPeoAccessSearchQuery,
  GetEmployeeForPeoAccessSearchQueryVariables
>;

type EmployeeBlacklistAutocompleteHookParams = {
  /** Optional for PEO Super Admins — the server falls back to their own PEO. */
  peoId?: Nullable<string>;
  skip?: boolean;
};

/**
 * Full-SSN employee search for the PEO staff Employee Blacklist field. Resolves against
 * MDM (not the CV DB), so employees with no CV-side record are findable too; selecting a
 * hit adds a `New`-kind chip carrying the server-issued token.
 */
export const useEmployeeBlacklistAutocomplete = (
  params: EmployeeBlacklistAutocompleteHookParams = {},
) => {
  return useAutocompleteFieldWithQuery(
    useSearchQuery,
    {
      getQueryItems: (data): EmployeeBlacklistItem[] => {
        const employee = data?.getEmployeeForPeoAccessSearch;
        if (!employee) return [];

        return [
          {
            kind: PeoStaffBlacklistedEmployeeKindEnum.New,
            id: employee.id,
            token: employee.token,
            last4OfSsn: employee.last4OfSsn,
            fullName: employee.fullName,
          },
        ];
      },
      queryOptions: {
        skip:
          params.skip ||
          // The server's SSN_REGEX wants exactly 9 digits, so gate on the cleaned digit
          // count — raw length would let a partially-typed dashed SSN through.
          (searchText => cleanStringFromNonDigits(searchText).length !== SSN_LENGTH),
      },
      queryFilter: searchText => {
        return {
          employeeSsn: cleanStringFromNonDigits(searchText),
          peoId: params.peoId ?? undefined,
        };
      },
    },
    {
      getOptionLabel,
    },
  );
};

const getOptionLabel = (option: EmployeeBlacklistItem): string => {
  return [formatMaskedSsnNumber(option.last4OfSsn), option.fullName].filter(isNotNil).join(': ');
};
